import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Empty, SpinLoading } from "antd-mobile";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { TakeOrderStatus } from "../../proto/nbchat-proto";
import { TakeOrderCard } from "../../components/Wallet";
import useGlobalTheme from "../../hooks/useGlobalTheme";
import { ToastError } from "../../components/Toast";

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight} - 100px);
  overflow: auto;
  background: #fff;
`;

const TypeTabs = styled.div`
  display: flex;
  justify-content: space-around;
  height: 40px;
  align-items: flex-end;
  background: #fff;
  border-bottom: 1px solid #eee;
`;

const TypeTab = styled.div<{ $active?: boolean }>`
  padding-bottom: 10px;
  cursor: pointer;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : "transparent")};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.inputPlaceholderColor || "#999"};
`;

const StatusTabs = styled.div`
  display: flex;
  justify-content: space-around;
  height: 60px;
  align-items: center;
  background: #fff;
`;

const StatusTab = styled.div<{ $active?: boolean }>`
  text-align: center;
  font-size: 12px;
  cursor: pointer;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.inputPlaceholderColor || "#999"};
`;

enum DetailType {
  BUY,
  SELL,
}

type StatusFilter = TakeOrderStatus | -1;

const TakeOrders = () => {
  const theme = useGlobalTheme();
  const {
    walletStore: { getTakeOrders, getTakeOrdersSold },
  } = useStores();
  const [type, setType] = useState<DetailType>(DetailType.BUY);
  const [status, setStatus] = useState<StatusFilter>(-1);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [isEnd, setIsEnd] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (reset = false) => {
    try {
      if (!reset && isEnd) return;
      setLoading(true);
      const current = reset ? 1 : page;
      const filters: Record<string, any> = { current, pageSize: 30 };
      if (status === TakeOrderStatus.EVALUATED) filters.status_in = [8, 13];
      else if (status === TakeOrderStatus.EVALUATED_SR) filters.status_in = [8, 14];
      else if (status === TakeOrderStatus.SETTLED) {
        filters.status_in =
          type === DetailType.BUY ? [13, 15] : [14, 15];
      } else if (status !== -1) filters.status_in = [status];

      const api = type === DetailType.BUY ? getTakeOrders : getTakeOrdersSold;
      const res = await api(filters);
      const nextData = res?.data || res?.list || [];
      const total = res?.total ?? nextData.length;
      const merged = reset ? nextData : [...data, ...nextData];
      setData(merged);
      setIsEnd(total === merged.length || nextData.length === 0);
      if (reset) setPage(1);
    } catch (e: any) {
      ToastError(e?.message || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData([]);
    setIsEnd(false);
    setPage(1);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status]);

  useEffect(() => {
    if (page > 1) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    setStatus(-1);
  }, [type]);

  const statusItems: { key: StatusFilter; label: string }[] = [
    { key: -1, label: "全部" },
    { key: TakeOrderStatus.PENDING, label: "待确认" },
    { key: TakeOrderStatus.MATCHING, label: "待付款" },
    { key: TakeOrderStatus.DELIVERING, label: "待发货" },
    {
      key:
        type === DetailType.BUY
          ? TakeOrderStatus.EVALUATED_SR
          : TakeOrderStatus.EVALUATED,
      label: "待评价",
    },
    { key: TakeOrderStatus.SETTLED, label: "已完成" },
  ];

  return (
    <PageWrap>
      <PageHeader title="我的订单" />
      <TypeTabs>
        <TypeTab
          $active={type === DetailType.BUY}
          onClick={() => setType(DetailType.BUY)}
        >
          我买入的
        </TypeTab>
        <TypeTab
          $active={type === DetailType.SELL}
          onClick={() => setType(DetailType.SELL)}
        >
          我卖出的
        </TypeTab>
      </TypeTabs>
      <StatusTabs>
        {statusItems.map((item) => (
          <StatusTab
            key={`${item.key}-${item.label}`}
            $active={status === item.key}
            onClick={() => setStatus(item.key)}
          >
            {item.label}
          </StatusTab>
        ))}
      </StatusTabs>
      <ScrollArea
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40 && !loading && !isEnd) {
            setPage((p) => p + 1);
          }
        }}
      >
        {!loading && data.length === 0 ? (
          <Empty description="暂无订单" style={{ padding: 40 }} />
        ) : (
          data.map((item, idx) => (
            <TakeOrderCard key={`${item.id || item.trans_no}-${idx}`} order={item} />
          ))
        )}
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
            <SpinLoading color={theme.colors.primary} />
          </div>
        )}
      </ScrollArea>
    </PageWrap>
  );
};

export default observer(TakeOrders);
